/**
 * Consent-gated Affonso affiliate pixel loader.
 *
 * The Affonso affiliate tracking pixel (cdn.affonso.io) is a marketing/analytics
 * tag that MUST NOT load until the user opts in to the "analytics" cookie
 * category (D-16 / ePrivacy). This module keys off the SAME cookie-consent state
 * and COOKIE_CONSENT_CHANGED_EVENT as PostHog — it does not maintain its own
 * consent flag — so a single consent decision governs both.
 *
 * Behaviour:
 *  - analytics granted  → inject the pixel <script> once (idempotent).
 *  - analytics withheld/revoked → remove the pixel and clear affonso_* cookies
 *    and storage so no first-party attribution state survives a revoke.
 */
import {
  getCookieConsent,
  COOKIE_CONSENT_CHANGED_EVENT,
} from "~/lib/cookie-consent-state";

export const AFFONSO_SCRIPT_ID = "affonso-pixel";
const AFFONSO_SRC = "https://cdn.affonso.io/js/pixel.min.js";
function getAffonsoDataKey(): string | undefined {
  return import.meta.env.VITE_AFFONSO_DATA_KEY;
}
const AFFONSO_COOKIE_DURATION = "30";

function analyticsConsented(): boolean {
  return getCookieConsent()?.analytics === true;
}

function pixelInstalled(): boolean {
  return (
    typeof document !== "undefined" &&
    document.getElementById(AFFONSO_SCRIPT_ID) !== null
  );
}

/** Inject the Affonso pixel <script> into <head> exactly once. */
export function installAffonsoPixel(): void {
  if (typeof document === "undefined") return;
  if (pixelInstalled()) return;
  const dataKey = getAffonsoDataKey();
  if (!dataKey) return;
  const script = document.createElement("script");
  script.id = AFFONSO_SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = AFFONSO_SRC;
  script.setAttribute("data-affonso", dataKey);
  script.setAttribute("data-cookie_duration", AFFONSO_COOKIE_DURATION);
  (document.head ?? document.documentElement).appendChild(script);
}

/** Remove the Affonso pixel and clear any first-party affonso_* state. */
export function removeAffonsoPixel(): void {
  if (typeof document === "undefined") return;
  document.getElementById(AFFONSO_SCRIPT_ID)?.remove();
  clearAffonsoState();
}

/** Best-effort removal of affonso_* cookies and web-storage keys on revoke. */
function clearAffonsoState(): void {
  if (typeof window === "undefined") return;
  try {
    for (const cookie of document.cookie ? document.cookie.split(";") : []) {
      const name = cookie.split("=")[0]?.trim();
      if (name && /^affonso/i.test(name)) {
        const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
        document.cookie = `${name}=; path=/; ${expiry}`;
        if (typeof location !== "undefined" && location.hostname) {
          document.cookie = `${name}=; path=/; domain=${location.hostname}; ${expiry}`;
        }
      }
    }
  } catch {
    /* cookie access may throw in restricted contexts — ignore */
  }
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && /affonso/i.test(key)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    /* storage may be unavailable — ignore */
  }
  try {
    sessionStorage.removeItem("alchema_affonso_tracked");
  } catch {
    /* ignore */
  }
}

/**
 * Reconcile the pixel with the CURRENT analytics consent decision. Safe to call
 * repeatedly — install and remove are both idempotent.
 */
export function syncAffonsoConsent(): void {
  if (analyticsConsented()) {
    installAffonsoPixel();
  } else {
    removeAffonsoPixel();
  }
}

/**
 * Install the consent listener. Applies current consent immediately, then
 * re-applies whenever cookie consent changes (COOKIE_CONSENT_CHANGED_EVENT).
 * Returns a cleanup function that removes the listener; on the server it is a
 * no-op returning a no-op cleanup.
 */
export function installAffonsoConsentListener(): () => void {
  if (typeof window === "undefined") return () => {};
  syncAffonsoConsent();
  const handler = () => syncAffonsoConsent();
  window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
}
